import { COLORS } from '@btime/constants'
import { dropLast, path } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'

import InputWithInterpolations from '../../../../../../../packages/kiti-editor/components/input/InputWithInterpolations'
import {
  findChildren,
  formatQuestionsByOrigin,
  formatQuestionsByOrigins,
  getQuestionsByType
} from '../../../../../../../utils/helpers/questions'
import { useChecklistForm, useChecklistFormActions, useSelectedQuestion } from '../../../../_form/ChecklistFormHandler'
import { SmallText, Title } from '../../../../../../../packages/kiti/components/presentation/Text'
import Separator from '../../../../../../../packages/kiti/components/helpers/Separator'
import DynamicInput from '../../../../../../../components/form/DynamicInput'
import Link from '../../../../../../../packages/kiti/components/actions/Link'
import { HorizontalLine } from '../../../../../../../components/shapes/Line'
import { canRenderInputForType } from '../_helpers/hideInputForType'
import { useAllFields } from '../../../../_load/FieldsLoader'
import { usePlaceTypes } from '../../../../../../../utils/helpers/placeTypes'

const DefaultValueSection = () => {
  const { t } = useTranslation('editChecklist')
  const { handleSelectedQuestionChange } = useChecklistFormActions()
  const selectedQuestion = useSelectedQuestion()
  const { selectedQuestionPath, ...form } = useChecklistForm() || {}

  const fields = useAllFields()
  const [types] = usePlaceTypes()
  const allPlaces = types.map(item => item.key) || []

  const availableFields = fields?.filter(field => field.type !== 'gallery') || []

  const siblingsPath = dropLast(1, selectedQuestionPath)
  const parentPath = dropLast(3, selectedQuestionPath)
  const questions = path(siblingsPath, form) || []
  const parentQuestions = path(parentPath, form) || []

  const shouldRender = canRenderInputForType(selectedQuestion?.questionType?.name)
  const shouldRenderDefaultValue = shouldRender('defaultValue')

  const parentQuestionName = parentQuestions.filter(question => {
    return question.children.some(child => child.reference === selectedQuestion?.reference)
  })[0]?.name

  const parentCategories = findChildren(form.categories, ({ reference }) => reference === selectedQuestion?.reference)
  const questionCategory = parentCategories?.selectedCategory?.name ?? t('noCategoryDefined')

  const questionDescription = parentQuestionName ? `${questionCategory} - ${parentQuestionName}` : questionCategory

  const assetQuestions = getQuestionsByType(questions, selectedQuestion, 'asset')
  const assetInterpolations = formatQuestionsByOrigin(
    assetQuestions,
    availableFields,
    'assets',
    questionDescription,
    questionCategory
  )

  const placeQuestions = getQuestionsByType(questions, selectedQuestion, 'place')
  const placeInterpolations = formatQuestionsByOrigins(
    placeQuestions,
    availableFields,
    allPlaces,
    questionDescription,
    questionCategory
  )

  const userQuestions = getQuestionsByType(questions, selectedQuestion, 'user')
  const userInterpolations = formatQuestionsByOrigin(
    userQuestions,
    availableFields,
    'contracts',
    questionDescription,
    questionCategory
  )

  const allInterpolations = assetInterpolations.concat(placeInterpolations, userInterpolations)

  const isQuestionTypeText = selectedQuestion.questionType?.name === 'text'

  const handleSetDefaultValue = value => {
    if (value === '') {
      handleSelectedQuestionChange('defaultValue', undefined)
      handleSelectedQuestionChange('data', undefined)
      return
    }
    const isInterpolation = !!value && !!value?.startsWith(':reply')
    if (isInterpolation) {
      handleSelectedQuestionChange('data', { ...selectedQuestion.data, defaultValue: value })
      handleSelectedQuestionChange('defaultValue', undefined)
      return
    }

    handleSelectedQuestionChange('defaultValue', value)
    handleSelectedQuestionChange('data', undefined)
  }

  if (!shouldRenderDefaultValue) return false

  return (
    <>
      <Separator weight={6} />

      <Title size="h6" color={COLORS.dark4}>
        {t('defaultValue')}
      </Title>
      <HorizontalLine thin lineColor={COLORS.light2} />
      <Separator weight={2} />

      {isQuestionTypeText ? (
        <InputWithInterpolations
          key={selectedQuestion.reference}
          value={selectedQuestion.data?.defaultValue ?? selectedQuestion.defaultValue}
          interpolations={allInterpolations}
          onChange={handleSetDefaultValue}
          clearButton
        />
      ) : (
        <DynamicInput
          key={selectedQuestion.reference}
          value={selectedQuestion.defaultValue}
          valueType={selectedQuestion.questionType?.name}
          onBlur={() => null}
          onChange={handleSelectedQuestionChange('defaultValue')}
          options={selectedQuestion.options}
          {...selectedQuestion.data}
        />
      )}

      <Separator weight={4} />

      {![null, undefined].includes(selectedQuestion.defaultValue) && !isQuestionTypeText && (
        <Link onClick={() => handleSelectedQuestionChange('defaultValue', undefined)}>
          <SmallText bold color={COLORS.red}>
            {t('removeDefaultValue')}
          </SmallText>
        </Link>
      )}
    </>
  )
}

export default DefaultValueSection
